// src/components/SplitText.js (or wherever you placed it)
import { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText as GSAPSplitText } from 'gsap/SplitText'; // Import as GSAPSplitText to avoid naming conflict
import { useGSAP } from '@gsap/react'; // Correct import for useGSAP hook

// IMPORTANT: Register GSAP plugins ONCE
// useGSAP is a React Hook, not a GSAP plugin, so it doesn't get registered here.
// GSAPSplitText is a utility class, not a plugin in the same way ScrollTrigger is.
gsap.registerPlugin(ScrollTrigger);

const SplitText = ({
  text,
  className = '',
  delay = 100, // Stagger delay in milliseconds
  duration = 0.6,
  ease = 'power3.out',
  splitType = 'chars', // 'chars', 'words', 'lines', or combination
  from = { opacity: 0, y: 40 },
  to = { opacity: 1, y: 0 },
  threshold = 0.1, // ScrollTrigger start threshold
  rootMargin = '-100px',
  textAlign = 'center',
  tag: Tag = 'p', // Renamed prop 'tag' to 'Tag' and defaulted to 'p'
  onLetterAnimationComplete
}) => {
  const ref = useRef(null);
  const animationCompletedRef = useRef(false);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // Ensure custom fonts are loaded before splitting to prevent layout shifts
  useEffect(() => {
    // Check if fonts are already loaded, or wait for them
    if (document.fonts.status === 'loaded') {
      setFontsLoaded(true);
    } else {
      document.fonts.ready.then(() => {
        setFontsLoaded(true);
      });
    }
  }, []); // Run once on mount

  useGSAP(
    () => {
      // Don't run if ref is null, text is empty, or fonts aren't loaded yet
      if (!ref.current || !text || !fontsLoaded) return;
      const el = ref.current;

      // Clean up previous SplitText instance if component updates
      if (el._rbsplitInstance) {
        try {
          el._rbsplitInstance.revert();
        } catch (_) {
          /* noop */
        }
        el._rbsplitInstance = null;
      }

      // Initialize SplitText
      const splitInstance = new GSAPSplitText(el, {
        type: splitType,
        smartWrap: true,
        linesClass: 'split-line',
        wordsClass: 'split-word',
        charsClass: 'split-char',
      });

      // Determine the targets for animation based on splitType
      let targets;
      if (splitType.includes('chars') && splitInstance.chars.length) {
        targets = splitInstance.chars;
      } else if (splitType.includes('words') && splitInstance.words.length) {
        targets = splitInstance.words;
      } else if (splitType.includes('lines') && splitInstance.lines.length) {
        targets = splitInstance.lines;
      } else {
        // Fallback if splitType is not recognized or yields no targets
        targets = splitInstance.chars || splitInstance.words || splitInstance.lines;
      }

      // If no valid targets, revert and exit
      if (!targets || targets.length === 0) {
        splitInstance.revert();
        el._rbsplitInstance = null;
        return;
      }

      // Set initial state (from props) immediately before animation starts
      gsap.set(targets, { ...from });

      // Configure ScrollTrigger start position
      const startPct = (1 - threshold) * 100;
      const marginMatch = /^(-?\d+(?:\.\d+)?)(px|em|rem|%)?$/.exec(rootMargin);
      const marginValue = marginMatch ? parseFloat(marginMatch[1]) : 0;
      const marginUnit = marginMatch ? marginMatch[2] || 'px' : 'px';
      const sign =
        marginValue === 0
          ? ''
          : marginValue < 0
            ? `-=${Math.abs(marginValue)}${marginUnit}`
            : `+=${marginValue}${marginUnit}`;
      const start = `top ${startPct}%${sign}`;

      // Create the animation tween
      const tween = gsap.to(
        targets,
        {
          ...to, // Animate to these properties
          duration,
          ease,
          stagger: delay / 1000, // Convert ms to seconds for stagger
          scrollTrigger: {
            trigger: el,
            start,
            once: true, // Animation plays only once
            fastScrollEnd: true,
            anticipatePin: 0.4
          },
          onComplete: () => {
            animationCompletedRef.current = true;
            onLetterAnimationComplete?.(); // Call the prop callback
          },
          willChange: 'transform, opacity', // Performance optimization
          force3D: true // Performance optimization
        }
      );

      el._rbsplitInstance = splitInstance; // Store instance for cleanup

      // Cleanup function for useGSAP
      return () => {
        // Kill ScrollTriggers associated with this element
        ScrollTrigger.getAll().forEach(st => {
          if (st.trigger === el) st.kill();
        });
        // Revert SplitText instance
        try {
          if (splitInstance) {
            splitInstance.revert();
          }
        } catch (_) {
          /* noop */
        }
        el._rbsplitInstance = null;
      };
    },
    {
      // Dependencies for useGSAP to re-run the animation
      dependencies: [
        text,
        delay,
        duration,
        ease,
        splitType,
        JSON.stringify(from), // Stringify objects for reliable dependency comparison
        JSON.stringify(to),
        threshold,
        rootMargin,
        fontsLoaded, // Re-run when fonts are confirmed loaded
        onLetterAnimationComplete // Re-run if callback reference changes
      ],
      scope: ref // Scopes the animation to the ref element
    }
  );

  // Apply base styles to the container element
  const style = {
    textAlign,
    overflow: 'hidden', // Hide overflow during animation
    display: 'inline-block', // Helps in fitting text without breaking lines unexpectedly
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    willChange: 'transform, opacity'
  };
  const classes = `split-parent ${className}`;

  // Dynamically render the specified HTML tag
  return (
    <Tag ref={ref} style={style} className={classes}>
      {text}
    </Tag>
  );
};

export default SplitText;
